<script setup lang="ts">
import { ManyPastOccurrencesByTypeQuery } from '~/queries/many/programme-types'

const props = defineProps<{
  title?: string
  description?: any
  useMask?: boolean | null
  image?: Media | null
  maskedMedia?: MaskedMedia | null
  viewOption?: 'title' | 'date' | 'grouped-by-year' | 'grouped-by-year-title-only' | 'thumbnails' | 'image'
  sortOption?: 'newest' | 'oldest' | 'az' | 'za'
  typeSlug: string
  typeId: string
}>()

const { locale } = useI18n()
const now = cacheableNow()

// The query is sorted newest-first, so page 1 (rendered on the server) holds the
// most recent occurrences and older ones stream in on the client.
const { docs, loading } = await usePaginatedOccurrences(
  `archive-${props.typeSlug}-${locale.value}`,
  ManyPastOccurrencesByTypeQuery,
  { now, typeId: props.typeId, locale: locale.value },
)

// A programme can have several recurrences, and each recurrence produces its own
// ProgrammeOccurrences doc. Group them back by programme so a programme with
// multiple past dates renders as a single card instead of one per occurrence.
// Recurrences are re-sorted ascending within each programme so recurrences[0]
// stays the earliest date, matching what programme-list uses as the card date.
const occurrences = computed(() => {
  const bySlug = new Map<string, any>()
  for (const o of docs.value) {
    const slug = o.programme?.slug
    if (!slug) continue
    if (!bySlug.has(slug)) {
      bySlug.set(slug, { id: slug, title: o.title, slug, image: o.programme?.image, description: o.programme?.description, recurrences: [] })
    }
    bySlug.get(slug).recurrences.push({ dateTimeStart: o.dateTimeStart, dateTimeEnd: o.dateTimeEnd, id: o.id })
  }
  for (const prog of bySlug.values()) {
    prog.recurrences.sort((a: any, b: any) => new Date(a.dateTimeStart).getTime() - new Date(b.dateTimeStart).getTime())
  }
  return [...bySlug.values()]
})

// Docs arrive newest-first, so that is the default; 'oldest' flips it.
const sorted = computed(() => {
  const list = [...occurrences.value]
  switch (props.sortOption) {
    case 'oldest': return list.reverse()
    case 'az': return list.sort((a: any, b: any) => a.title.localeCompare(b.title))
    case 'za': return list.sort((a: any, b: any) => b.title.localeCompare(a.title))
    default: return list
  }
})
</script>

<template>
  <section
    class="mx-1"
    v-if="sorted.length"
  >
    <h2 v-if="title" 
      class="text-md mb-1 container-d"
    >
      {{ title }}
    </h2>

    <div 
      class="container-d mb-1"
      v-if="image?.id || maskedMedia?.id"
    >
      <core-maskable-media
        :use-mask="useMask"
        :image="image"
        :masked-media="maskedMedia"
      />
    </div>

    <div v-if="hasRichTextContent(description)" class="container-d mb-0_35">
      <core-rich-text :content="description" />
    </div>

    <div v-if="loading" class="container-d">
      {{ $t('labels.loading') }}
    </div>

    <pages-programme-type-programme-list
      v-else-if="sorted.length"
      :programmes="sorted"
      :type-slug="typeSlug"
      :view-option="viewOption"
      :class="{
        'mt-1': viewOption === 'image' && !image?.id && !maskedMedia?.id && !description
      }"
    />
  </section>
</template>
